<script setup lang="ts">
  import { computed, onBeforeUnmount, ref } from 'vue';

  import type { EmailSender, EmailSenderProvider } from '@stuut/api/models';
  import GoogleLogo from '@ui/assets/google.svg?component';
  import MicrosoftLogo from '@ui/assets/microsoft-logo-colored.svg?component';
  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { toast } from '@ui/components/sonner';
  import { popupCenter } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import { useProfile } from '@/store/profile';

  interface Props {
    existingEmailSender?: EmailSender;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'success', emailSender: EmailSender): void;
  }>();

  const profileStore = useProfile();

  type LinkStep = 'prompt' | 'provider-select' | 'authorization' | 'validation';

  const OAUTH_MAX_ALLOTTED_TIME = 1000 * 60 * 3;
  const OAUTH_WINDOW_CHECK_INTERVAL = 100;
  const NYLAS_OAUTH_REDIRECT_URI = `${window.location.origin}/oauth/nylas`;

  // Read-only scopes for hybrid orgs (Nylas for reading, SendGrid for sending)
  const READ_ONLY_SCOPES: Record<string, string[]> = {
    google: ['https://www.googleapis.com/auth/gmail.readonly'],
    microsoft: ['https://graph.microsoft.com/Mail.Read'],
  };

  const linkStep = ref<LinkStep>('prompt');
  const selectedProvider = ref<EmailSenderProvider | null>(null);
  const errorMessage = ref<string>();
  const authorizationCode = ref<string>();

  const isHybridOrg = computed(
    () => profileStore.profile.organization?.hybrid_mode_enabled ?? false,
  );

  onBeforeUnmount(() => {
    window.removeEventListener('message', onMessage);
  });

  const onMessage = (event) => {
    if (event.data?.type === 'oauth.nylas' && event.data.code) {
      authorizationCode.value = event.data.code;
      handleOAuthCallback();
    }
  };

  window.addEventListener('message', onMessage);

  const handleOAuthCallback = () => {
    if (linkStep.value === 'validation') return;
    validateOAuth();
  };

  const createLink = () => {
    errorMessage.value = undefined;

    if (linkStep.value === 'validation') return;

    if (isHybridOrg.value && !props.existingEmailSender) {
      linkStep.value = 'provider-select';
      return;
    }

    obtainAuthorization();
  };

  const selectProvider = (provider: EmailSenderProvider) => {
    selectedProvider.value = provider;
    obtainAuthorization();
  };

  const obtainAuthorization = async () => {
    if (linkStep.value === 'authorization') return;
    linkStep.value = 'authorization';

    try {
      const provider = props.existingEmailSender?.provider ?? selectedProvider.value;
      const scopes = isHybridOrg.value && provider ? READ_ONLY_SCOPES[provider] : null;

      const response = await stuut.emailSenders.getAuthorizationUrl(
        NYLAS_OAUTH_REDIRECT_URI,
        provider,
        scopes,
      );

      if (response.url) {
        const popup = popupCenter(response.url, 'oauth_popup', 500, 450);
        if (popup) {
          popup.focus();

          let passedTime = 0;
          const interval = setInterval(() => {
            try {
              if (popup.closed || passedTime >= OAUTH_MAX_ALLOTTED_TIME) {
                clearInterval(interval);

                if (passedTime >= OAUTH_MAX_ALLOTTED_TIME && !popup.closed) {
                  popup.close();
                  errorMessage.value = 'Max time reached.';
                  linkStep.value = 'prompt';
                } else {
                  setTimeout(() => {
                    if (!authorizationCode.value) {
                      errorMessage.value = 'Failed to authenticate';
                      linkStep.value = 'prompt';
                    }
                  });
                }
              }
            } catch (error) {
              console.error(error);
              errorMessage.value = 'An error occurred';
              linkStep.value = 'prompt';
              clearInterval(interval);
            }

            passedTime += OAUTH_WINDOW_CHECK_INTERVAL;
          }, OAUTH_WINDOW_CHECK_INTERVAL);
          return;
        }

        console.error('Failed to open popup window for OAuth authorization');
      } else {
        console.error('Authorization URL not provided');
      }
    } catch (error) {
      console.error('Error initiating OAuth flow', error);
    }

    errorMessage.value = 'An error occurred';
    linkStep.value = 'prompt';
  };

  const createUpdateEmailSender = async () => {
    const data = {
      authorization_code: authorizationCode.value,
      redirect_uri: NYLAS_OAUTH_REDIRECT_URI,
    };

    if (props.existingEmailSender?.is_nylas_grant_expired) {
      return stuut.emailSenders.reconnectGrant(
        props.existingEmailSender?.id,
        data.authorization_code,
        data.redirect_uri,
      );
    }
    return stuut.emailSenders.create(data);
  };

  const validateOAuth = async () => {
    if (linkStep.value === 'validation') return;
    linkStep.value = 'validation';

    createUpdateEmailSender()
      .then((emailSender) => {
        emits('success', emailSender);
        toast.success('Your email has been linked.');
      })
      .catch((err) => {
        errorMessage.value = err.clientMessage;
        authorizationCode.value = '';
        linkStep.value = 'prompt';
      });
  };
</script>

<template>
  <div class="px-4 pt-6 flex flex-col h-full">
    <div class="flex flex-col items-center mt-4 mb-6 space-y-2">
      <div class="flex flex-row items-center space-x-4">
        <GoogleLogo class="h-6 w-6" />

        <MicrosoftLogo class="h-6 w-6" />
      </div>

      <!--      <i class="fa far fa-link text-2xl"/> -->
      <p class="font-semibold text-lg">Link Email</p>
    </div>

    <div class="flex-1">
      <p v-if="linkStep === 'prompt'" class="text-muted-foreground text-sm text-center">
        Link your email with Stuut to enable easy and seamless communication with your customers.
      </p>

      <div
        v-else-if="linkStep === 'provider-select'"
        class="w-full h-full flex flex-col items-center py-4"
      >
        <p class="text-muted-foreground text-sm text-center mb-6">Select your email provider</p>
        <div class="flex flex-col space-y-3 w-full max-w-xs">
          <Button
            variant="outline"
            class="flex items-center justify-center gap-3 py-6"
            @click="selectProvider('google')"
          >
            <GoogleLogo class="h-5 w-5" />
            <span>Google</span>
          </Button>
          <Button
            variant="outline"
            class="flex items-center justify-center gap-3 py-6"
            @click="selectProvider('microsoft')"
          >
            <MicrosoftLogo class="h-5 w-5" />
            <span>Microsoft</span>
          </Button>
        </div>
      </div>

      <div
        v-else-if="linkStep === 'authorization'"
        class="w-full h-full flex flex-col items-center py-8"
      >
        <div class="space-y-4 text-center">
          <LoadingSpinner class="text-xl far" />
          <p class="font-semibold">Waiting For Authentication...</p>
        </div>
      </div>

      <div
        v-else-if="linkStep === 'validation'"
        class="w-full h-full flex flex-col items-center py-8"
      >
        <div class="space-y-4 text-center">
          <LoadingSpinner class="text-xl far" />
          <p class="font-semibold">Linking Your Email...</p>
        </div>
      </div>
    </div>

    <div>
      <p
        v-if="errorMessage"
        class="text-destructive text-xs mb-3 text-center line-clamp-2 font-semibold"
      >
        {{ errorMessage }}
      </p>

      <div v-if="linkStep === 'prompt'" class="flex justify-center">
        <Button class="w-full" @click="createLink"> Continue </Button>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
